<template lang="html">
    <div :id="id">
        <report-section
            id="query-monthly-hydrology"
            class="monthly-hydrology-subsection"
            :title="queryWatershedTitle"
        >
            <template #header-image>
                <map-marker
                    v-if="!pdf"
                    class="map-marker query-watershed is-hidden-mobile"
                >
                    Query Watershed Marker
                </map-marker>
            </template>
            <p>
                Hydrologic models<sup>
                    <footnote-tooltip
                        :footnote-number="6"
                    />
                </sup> have been developed to produce estimates of mean monthly flows. The Province of BC’s
                <i>Environmental Flow Needs Policy</i><sup>
                    <footnote-tooltip
                        :footnote-number="7"
                    />
                </sup> has been applied to these estimates, identifying risk management levels<sup>
                    <footnote-tooltip
                        :footnote-number="8"
                    />
                </sup> to support water management decisions. Information on active water licences and approvals
                (collectively, ‘allocations’) in the watershed have been extracted and summarized from government
                databases<sup>
                    <footnote-tooltip
                        :footnote-number="9"
                    />
                </sup> and integrated with the hydrology model data and risk management level calculations, to account
                for the volume of water already allocated.
            </p>
            <p>
                In the chart below, the height of each column represents the mean monthly discharge - the long term,
                estimated average flow for that month of the year. The dark, medium, and light blue areas of the
                columns show the potential amount of water allocations within each risk management level. When
                allocations exist in the watershed, a red box hangs down from the top of each column to represent the
                volume of existing allocations in the context of mean monthly supply.
                <span class="is-hidden-mobile">
                    The table below corresponds to the data shown on the chart.
                </span>
            </p>
            <monthly-hydrology-chart
                class="monthly-hydrology-chart"
                :data="getChartData()"
                :provided-options="options"
                :pdf="pdf"
            />
            <monthly-hydrology-table
                class="monthly-hydrology-table is-hidden-mobile page-break-before"
                :data="getTableData()"
            />
        </report-section>

        <report-section
            id="downstream-monthly-hydrology"
            :title="downstreamWatershedTitle"
            :class="['monthly-hydrology-subsection', { 'is-hidden-mobile': !pdf }]"
        >
            <template #header-image>
                <map-marker
                    v-if="!pdf"
                    class="map-marker downstream-watershed"
                >
                    Downstream Watershed Marker
                </map-marker>
            </template>
            <p>
                Similar to the previous section, which described the water supply and demand for the location that you
                selected, this section describes the water supply and demand for the downstream basin. The hydrology
                model and risk management calculations are the exact same, but the calculation logic for existing
                allocations is different, taking into account non-consumptive, or ‘flow-through’ water rights.<sup>
                    <footnote-tooltip
                        :footnote-number="9"
                    />
                </sup>
            </p>
            <monthly-hydrology-chart
                class="monthly-hydrology-chart"
                :data="getChartData(true)"
                :provided-options="options"
                :pdf="pdf"
            />
            <monthly-hydrology-table
                class="monthly-hydrology-table page-break-before"
                :data="getTableData(true)"
            />
        </report-section>
    </div>
</template>

<script>
import MapMarker from '@foundryspatial/map-marker';
import sectionMixin from '@foundryspatial/report-section/mixin';
import numeral from 'numeral';

import MonthlyHydrologyChart from '@/components/charts/MonthlyHydrologyChart.vue';
import MonthlyHydrologyTable from '@/components/MonthlyHydrologyTable.vue';

export default {
    name: 'MonthlyHydrologySection',

    components: {
        MapMarker,
        MonthlyHydrologyChart,
        MonthlyHydrologyTable,
    },

    mixins: [
        sectionMixin,
    ],

    props: {
        // true on the PDF report
        pdf: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
    }),

    computed: {
        options() {
            return {
                yAxisLabel: 'Monthly Discharge (m³/s)',
                unitOfMeasurement: 'm³/s',
                sortTooltips: true,
            };
        },

        queryWatershedTitle() {
            return `Monthly Water Supply and Demand - ${this.reportData.overview.watershedName}`;
        },

        downstreamWatershedTitle() {
            return `Monthly Water Supply and Demand - ${this.reportData.overview.mgmt_name}`;
        },
    },

    methods: {
        /**
         * Get the data needed by MonthlyHydrologyChart for the appropriate watershed.
         *
         * @param {boolean} downstream - optional value to indicate if we want the chart data for the upstream or
         *                               downstream watershed
         * @returns {object} - all data needed by MonthlyHydrologyChart
         */
        getChartData(downstream = false) {
            // get the appropriate data from the report
            if (downstream) {
                return this.reportData.downstreamMonthlyHydrology;
            }
            return this.reportData.queryMonthlyHydrology;
        },

        /**
         * Get the data needed by MonthlyHydrologyTable for the appropriate watershed.
         *
         * @param {boolean} downstream - optional value to indicate if we want the table data for the upstream or
         *                               downstream watershed
         * @returns {object} - all data needed by MonthlyHydrologyTable
         */
        getTableData(downstream = false) {
            // get the appropriate data from the report
            let tableData;
            if (downstream) {
                tableData = this.reportData.downstreamMonthlyHydrology;
            } else {
                tableData = this.reportData.queryMonthlyHydrology;
            }

            const {
                rm1,
                rm2,
                rm3,
                monthlyDischargePercentages,
                monthlyFlowSensitivies,
                waterLicenceMonthlyDisplay,
                shortTermAllocationMonthlyDisplay,
                monthlyDischarge,
            } = tableData;

            return {
                '% of MAD': monthlyDischargePercentages,
                'Flow Sensitivity': monthlyFlowSensitivies,
                'Existing Water Licences* (m³/s)': waterLicenceMonthlyDisplay,
                'Existing Short Term Approvals* (m³/s)': shortTermAllocationMonthlyDisplay,
                'Mean Monthly Discharge (m³/s)': monthlyDischarge,
                'Potential Allocation (m³/s, Risk Mgmt 1)': rm1,
                'Potential Allocation (m³/s, Risk Mgmt 2)': rm2,
                'Potential Allocation (m³/s, Risk Mgmt 3)': this.formatRm3Values(rm3),
            };
        },

        /**
         * Format rm3 values properly. We need to round them and place the greater than or equal to sign in front of
         * them. The true rm3 value is not known, but we know it will be greater than the given value.
         *
         * @param {Array} values - rm3 values to be formatted
         * @returns {Array} - array of rm3 values that have been formatted for display
         */
        formatRm3Values(values) {
            return values.map((value) => {
                const roundedValue = numeral(value).format('0,0.00');
                return `≥ ${roundedValue}`;
            });
        },
    },
};
</script>

<style lang="scss">
.monthly-hydrology-subsection {
    .map-marker {
        width: 2.5rem;
        margin-right: 1rem;

        &.query-watershed {
            .color-fill {
                fill: $color-watershed-query;
            }
        }

        &.downstream-watershed {
            .color-fill {
                fill: $color-watershed-mgmt;
            }
        }
    }

    .monthly-hydrology-chart {
        margin-bottom: 1rem;
    }
}

.static-report .monthly-hydrology-subsection {
    .monthly-hydrology-chart {
        flex-direction: column;

        .header {
            font-size: 1.25rem;
            line-height: 1.25rem;
        }

        .pdf-legend {
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: space-between;

            .pdf-legend-col {
                height: fit-content;
                width: fit-content;
                margin: 0;

                .legend-item {
                    width: fit-content;

                    .row > * {
                        margin: 0 0.3em;
                    }

                    .visual-container {
                        height: 1em;
                        width: 1em;

                        .color-box {
                            height: 1em;
                        }
                    }
                }
            }
        }

        .chart {
            width: 100%;
            height: 350px;
        }
    }

    .monthly-hydrology-table {
        font-size: 0.7rem;

        td {
            font-size: 0.625rem;
        }

        .th-wrap {
            width: 38px;
        }
    }
}
</style>
